import { isWeekend } from 'helpers/date';
import { parseJSON } from 'helpers/string';
import { nextFrame } from 'helpers/timing';
import dayjs from 'lib/dayjs';
import { Datepicker } from 'vanillajs-datepicker';
import type { DatepickerOptions, DatepickerOptionsFormat } from 'vanillajs-datepicker/Datepicker';

export default class DatePickerElement extends HTMLElement {
  input: HTMLInputElement | null = null;
  datepicker: Datepicker | null = null;
  private controller: AbortController;

  constructor() {
    super();
    this.onDateChange = this.onDateChange.bind(this);
    this.onHide = this.onHide.bind(this);
    this.reset = this.reset.bind(this);
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.input = this.querySelector('input[type="text"]');

    if (this.input) {
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('spellcheck', 'false');
      this.input.addEventListener('changeDate', (event) => this.onDateChange(event as CustomEvent), {
        signal: this.controller.signal,
      });
      this.input.addEventListener('hide', this.onHide, { signal: this.controller.signal });
      this.value = this.input.value;
      this.datepicker = new Datepicker(this.input, this.options);
    } else {
      this.addEventListener('changeDate', (event) => this.onDateChange(event as CustomEvent), {
        signal: this.controller.signal,
      });
      this.setAttribute('data-date', this.value || '');
      this.datepicker = new Datepicker(this, this.options);
    }

    this.form?.addEventListener('reset', this.reset, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.datepicker?.destroy();
  }

  static get observedAttributes(): string[] {
    return ['value', 'disabled', 'min'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    switch (name) {
      case 'value':
        if (newValue !== null) {
          this.datepicker?.setDate(newValue);
        }
        break;
      case 'disabled':
        if (!this.input) break;
        this.input.disabled = newValue !== null;
        break;
      case 'min':
        if (newValue !== null) {
          this.datepicker?.setOptions({ minDate: newValue });
        }
        break;
      default:
        break;
    }
  }

  onDateChange(event: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('date-picker:change', { detail: { value: event.detail.date, ...event.detail }, bubbles: true })
    );
  }

  onHide() {
    if (this.input?.value.length === 0 && this.required) {
      this.reset();
    }
  }

  async reset(options = {}) {
    // Force set input value to the specified datetime format.
    await nextFrame();
    if (this.input) {
      this.input.value = dayjs(this.value).format(this.formatString);
    }
    this.datepicker?.setDate(this.value, options);
  }

  refresh() {
    this.datepicker?.refresh();
  }

  get value() {
    return this.getAttribute('value');
  }

  set value(value: string | null | undefined) {
    if (!value) return;
    this.setAttribute('value', dayjs(value).toISOString());
  }

  get date() {
    return this.datepicker?.getDate();
  }

  get minDate() {
    return this.getAttribute('min') || undefined;
  }

  set minDate(value: string | undefined) {
    if (!value) return;
    this.setAttribute('min', dayjs(value).toISOString());
  }

  get maxDate() {
    return this.getAttribute('max') || undefined;
  }

  get required() {
    return this.hasAttribute('required');
  }

  get format(): DatepickerOptionsFormat {
    return {
      toValue: (date: string | Date) => dayjs(date).toDate(),
      toDisplay: (date: string | Date) => dayjs(date).format(this.formatString),
    };
  }

  get buttonClass() {
    const className = this.getAttribute('data-button-class') || '';
    return `btn ${className}`;
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value: boolean) {
    this.toggleAttribute('disabled', value);
  }

  get options(): DatepickerOptions {
    const options = parseJSON(this.getAttribute('data-options'), {});

    return {
      autohide: true,
      buttonClass: this.buttonClass,
      dateDelimiter: '.', // format string cannot include the `dateDelimiter`. See: https://mymth.github.io/vanillajs-datepicker/#/options?id=format
      format: this.format,
      minDate: this.minDate,
      maxDate: this.maxDate,
      nextArrow: '<i class="bi bi-arrow-right"></i>',
      prevArrow: '<i class="bi bi-arrow-left"></i>',
      beforeShowDay: (date: Date) => {
        if (this.disableWeekends && isWeekend(dayjs(date))) {
          return {
            classes: 'disabled',
          };
        }
      },
      ...options,
    };
  }

  // List of all available formats: https://day.js.org/docs/en/display/format#list-of-all-available-formats
  get formatString() {
    return this.getAttribute('data-format') || 'MMMM D, YYYY';
  }

  get disableWeekends() {
    const attrName = 'disable-weekends';
    return this.hasAttribute(attrName) && this.getAttribute(attrName) !== 'false';
  }

  get form() {
    return this.closest('form');
  }
}
